export const listLineRegex = /^\s*(?:[-*+]|\d+\.)\s+/;
export const anchorLineRegex =
  /<div data-diff-hunk-(start|end)="[^"]+" class="diff-hunk-(start|end)"><\/div>/;
export const anchorBlockRegex =
  /(?:\r?\n){0,2}<div data-diff-hunk-(?:start|end)="[^"]+" class="diff-hunk-(?:start|end)"><\/div>(?:\r?\n){0,2}/g;
export const tableSeparatorRegex = /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*$/;

export type DiffLineEntry = { text: string };

export const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const tightenListSpacing = (entries: DiffLineEntry[]): DiffLineEntry[] => {
  const result: DiffLineEntry[] = [];

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (!entry) continue;
    const isBlankLine = entry.text.trim().length === 0;

    if (isBlankLine) {
      const previousEntry = result.length > 0 ? result[result.length - 1] : null;
      const nextEntry = entries[i + 1] ?? null;

      if (
        previousEntry &&
        nextEntry &&
        listLineRegex.test(previousEntry.text) &&
        listLineRegex.test(nextEntry.text)
      ) {
        continue;
      }
    }

    result.push(entry);
  }

  const compacted: DiffLineEntry[] = [];
  for (const entry of result) {
    const isBlankLine = entry.text.trim().length === 0;
    if (isBlankLine) {
      const last = compacted[compacted.length - 1];
      if (last && last.text.trim().length === 0) {
        continue;
      }
    }
    compacted.push(entry);
  }

  return compacted;
};

export const getNormalizedLines = (lines: string[]): string[] =>
  lines.map((line) => line.replace(/\s+/g, " ").trim()).filter(Boolean);

export const isAnchorLine = (line: string): boolean => anchorLineRegex.test(line);

export const normalizeTableBlocks = (content: string): string => {
  const lines = content.split("\n");
  const normalized: string[] = [];

  for (let i = 0; i < lines.length; ) {
    const line = lines[i];

    if (line === undefined || line.trim() === "") {
      normalized.push("");
      i++;
      continue;
    }

    if (line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      let j = i;
      while (j < lines.length) {
        const currentLine = lines[j];
        if (currentLine === undefined || !currentLine.trim().startsWith("|")) {
          break;
        }
        tableLines.push(currentLine);
        j++;
      }
      const hasSeparator = tableLines.some((line) => tableSeparatorRegex.test(line));
      if (!hasSeparator && tableLines.length > 0) {
        const rows = tableLines.map((raw) => {
          let body = raw.trim();
          const hasLeadingBar = body.startsWith("|");
          const hasTrailingBar = body.endsWith("|");
          if (hasLeadingBar) body = body.slice(1);
          if (hasTrailingBar) body = body.slice(0, -1);
          return body.split("|").map((cell) => escapeHtml(cell.trim()));
        });

        const tableHtml = `\n<table class="diff-table"><tbody>${rows
          .map((row) => `<tr>${row.map((cell) => `<td>${cell || "&nbsp;"}</td>`).join("")}</tr>`)
          .join("")}</tbody></table>\n`;
        normalized.push(tableHtml);
      } else {
        if (!hasSeparator && tableLines.length === 1 && tableLines[0]) {
          normalized.push(tableLines[0]);
        } else {
          if (normalized.length > 0 && normalized[normalized.length - 1] !== "") {
            normalized.push("");
          }
          normalized.push(...tableLines);
          normalized.push("");
        }
      }
      i = j;
      continue;
    }

    normalized.push(line);
    i++;
  }

  return normalized.join("\n");
};

export const removeQuotes = (content: string): string => {
  const trimmed = content.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  return content;
};

export const stripAnchors = (content: string): string => {
  return content.replace(anchorBlockRegex, "\n\n").replace(/\n{3,}/g, "\n\n");
};
